import React, { Component } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import PayPalButton from 'components/Core/Payment/Buttons/AppConnectButton/PayPalAppConnectButton';
import PayButton from 'components/Core/Payment/Buttons/PayButton/PayButton';
import PaymentForm from 'components/Core/Payment/PaymentForm/PaymentForm';
import DeclineEstimateModal from 'components/Estimate/Declined/DeclineModal/DeclineEstimateModal';
import Button from 'components/Shared/Button/Button';
import PaymentDisclaimer from 'components/Shared/PaymentDisclaimer/PaymentDisclaimer';
import Separator from 'components/Shared/Separator/Separator';
import SpinnerCentered from 'components/Shared/Spinner/SpinnerCentered/SpinnerCentered';
import { isInEnhancementsHoldoutExperiment } from 'store/ixp/selector';
import { modalActions } from 'store/modal/slice';
import { onPayButtonClicked } from 'store/payment/slice';
import { saleSelectors } from 'store/sale/selectors';
import { setStatusDeclined } from 'store/sale/slice';
import { RootState } from 'store/store';
import { fetchWallets } from 'store/wallet/slice';
import { breakpoints } from 'styles/cp';
import { Auth } from 'types/Auth';
import { Customizations } from 'types/CompanyInfo';
import { Config } from 'types/Config';
import { Modal } from 'types/Modal';
import { Nanopay } from 'types/Nanopay';
import { Payment } from 'types/Payment';
import { Wallet } from 'types/Wallet';
import { TXN_MAP } from 'types/constants';

type DepositPaymentProps = {
  currency: string;
  auth: Pick<Auth, 'isUserSignedIn'>;
  featureFlags: object;
  config: Config;
  wallet: Pick<
    Wallet,
    | 'userWallets'
    | 'enabledPaymentMethods'
    | 'enabledCCTypes'
    | 'isDetectingApplePayStatus'
    | 'isDetectingPayPalStatus'
    | 'fetchWalletStatus'
    | 'cardOnFile'
  >;
  payment: Pick<
    Payment,
    | 'isPayPalCommerceInvoice'
    | 'paymentStatus'
    | 'paymentMethodType'
    | 'amount'
    | 'paymentError'
    | 'isAmountValid'
    | 'payPalProcessor'
  >;
  dueDate: string;
  fetchWallets: Function;
  recurringInfo?: object;
  showModal: (payload: Pick<Modal, 'component' | 'componentProps'>, state?: Partial<Modal>) => void;
  hideModal: () => void;
  setStatusDeclined: () => void;
  nanopay: Pick<Nanopay, 'hasNanopayConnected' | 'payingWithAccount'>;
  onPayButtonClicked: () => void;
  customizations: Customizations;
  isInEnhancementsHoldoutExperiment: boolean;
};

class DepositPayment extends Component<DepositPaymentProps, {}> {
  constructor(props: DepositPaymentProps) {
    super(props);
  }

  componentDidMount() {
    const { auth, wallet, fetchWallets } = this.props;
    if (auth.isUserSignedIn && !wallet.userWallets) {
      fetchWallets();
    }
  }

  render() {
    const {
      currency,
      config,
      dueDate,
      wallet: { isDetectingApplePayStatus, isDetectingPayPalStatus, fetchWalletStatus },
      payment: { isPayPalCommerceInvoice, paymentStatus },
      auth,
      recurringInfo,
      showModal,
      hideModal,
      setStatusDeclined,
      isInEnhancementsHoldoutExperiment,
    } = this.props;

    const isFetchingWallet =
      fetchWalletStatus === 'fetching' || (!fetchWalletStatus && auth.isUserSignedIn);
    if (isDetectingApplePayStatus || isDetectingPayPalStatus || isFetchingWallet) {
      return <SpinnerCentered />;
    }
    const { paymentMethodType } = this.props.payment;
    const renderPaymentButton = () => {
      if (isPayPalCommerceInvoice && paymentMethodType === 'pp') {
        return (
          <PayPalButton
            // @ts-expect-error
            // type mismatch
            config={config}
            wallet={this.props.wallet}
            auth={this.props.auth}
            payment={this.props.payment}
            featureFlags={this.props.featureFlags}
            payMessage={
              <FormattedMessage id="DEPOSIT_PAY_BUTTON_MESSAGE" defaultMessage="Accept and pay" />
            }
            payMessageType={
              <FormattedMessage id="DEPOSIT_PAYMENT_CAPTION" defaultMessage="deposit" />
            }
          />
        );
      }
      if (paymentMethodType === 'nanopay') {
        const { nanopay } = this.props;
        if (nanopay) {
          const { hasNanopayConnected, payingWithAccount } = nanopay;
          if (!hasNanopayConnected || !payingWithAccount || !payingWithAccount.id) {
            return null;
          }
        }
      }
      if (!isPayPalCommerceInvoice) {
        return (
          <PayButton
            currency={currency}
            config={config}
            wallet={this.props.wallet}
            auth={this.props.auth}
            payment={this.props.payment}
            featureFlags={this.props.featureFlags}
            onPayButtonClicked={this.props.onPayButtonClicked}
            recurringInfo={recurringInfo}
            payMessage={<FormattedMessage id="PAYNOW_PAY_BUTTON_LABEL" defaultMessage="Pay" />}
            customizations={this.props.customizations}
          />
        );
      }
      return null;
    };
    const declineHandler = () => {
      const ModalContent = () => (
        <DeclineEstimateModal hide={hideModal} onCancel={hideModal} onDecline={setStatusDeclined} />
      );
      showModal({
        component: ModalContent,
        componentProps: {
          closeOnClickOutSide: true,
          closeOnEsc: true,
          showCloseButton: false,
        },
      });
    };
    return (
      <div>
        <PaymentForm
          isPPCheckoutExperiment={false}
          isPPExperimentTreatmentA={false}
          isPPExperimentTreatmentB={false}
          {...this.props}
        />
        <Separator height={15} />
        <div className="buttons">
          {renderPaymentButton()}
          <div className="buttons-spacing mobile-hide" />
          <Button
            className="mobile-hide"
            aria-label="Decline"
            disabled={paymentStatus === TXN_MAP.STATUS.IN_PROGRESS}
            buttonType="tertiary"
            size="standard"
            width="100%"
            onClick={declineHandler}
          >
            <FormattedMessage id="DECLINE" defaultMessage="Decline" />
          </Button>
          <PaymentDisclaimer
            payment={this.props.payment}
            currency={currency}
            dueDate={dueDate}
            isInEnhancementsHoldoutExperiment={isInEnhancementsHoldoutExperiment}
          />
        </div>
        <style jsx>{`
          text-align: left;

          .buttons-spacing {
            height: 16px;
          }

          .bugs {
            ul {
              list-style: none;
              padding: 10px 0;
              margin: 0;
              text-align: center;

              :global(li) {
                display: inline-block;

                &:not(first-child) {
                  margin-left: 5px;
                }
              }
            }
          }

          .buttons {
            @media screen and (max-width: ${breakpoints.md}) {
              position: ${paymentMethodType ? 'fixed' : 'inherit'};
              padding: 15px 25px 0;
              left: 0;
              right: 0;
              bottom: 0;
              background-color: white;
            }
          }

          :global(.mobile-hide) {
            @media screen and (max-width: ${breakpoints.md}) {
              display: none;
            }
          }
        `}</style>
      </div>
    );
  }
}

function mapStateToProps(store: RootState) {
  const { sale, config, payment, wallet, auth, ixp, featureFlags, companyInfo } = store;
  return {
    currency: saleSelectors.currencySelector(sale),
    dueDate: saleSelectors.dueDateSelector(sale),
    auth,
    config,
    payment,
    wallet,
    featureFlags,
    ixp,
    recurringInfo: saleSelectors.getRecurringInfo(sale),
    customizations: companyInfo?.customizations,
    isInEnhancementsHoldoutExperiment: isInEnhancementsHoldoutExperiment(store),
  };
}

export default injectIntl(
  // @ts-ignore
  connect(mapStateToProps, {
    onPayButtonClicked,
    showModal: modalActions.show,
    hideModal: modalActions.hide,
    setStatusDeclined,
    fetchWallets,
    // @ts-ignore
  })(DepositPayment)
);
